import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Input, Modal, Spin, Table, Tooltip, message } from "antd";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import { deleteSprKassa, fetchSprKassaList } from "./spr-kassa-api";
import SprKassaDrawer from "./spr-kassa-drawer";
import { VID_KKM_LABELS, getLawFlag, normalizeKassa } from "./spr-kassa-utils";
import "./spr-kassa.css";

const qFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  return src.has("q") ? src.get("q") ?? "" : "";
};

/**
 * Справочник касс — список + drawer-карточка (Delphi TFrmSprKassa).
 */
const SprKassa = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = useMemo(() => qFromSearchParams(searchParams), [searchParams]);

  const law = useSelector((state) => state.user.session?.law);
  const canView = getLawFlag(law, 48) === 1;
  const canEdit = getLawFlag(law, 49) === 1;

  const [items, setItems] = useState([]);
  const [nameFilter, setNameFilter] = useState(() => urlQ);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerIsNew, setDrawerIsNew] = useState(false);

  const listReqRef = useRef(0);
  const skipUrlSyncRef = useRef(false);

  useEffect(() => {
    const q = (nameFilter || "").trim();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (searchParams.toString() === next.toString()) return;
    skipUrlSyncRef.current = true;
    setSearchParams(next, { replace: true });
  }, [nameFilter, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = qFromSearchParams(searchParams);
    setNameFilter((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  const loadList = useCallback(() => {
    const requestId = ++listReqRef.current;
    setLoadingList(true);
    setError("");
    return fetchSprKassaList()
      .then((data) => {
        if (requestId !== listReqRef.current) return;
        const rows = Array.isArray(data)
          ? data.map(normalizeKassa).filter((r) => r.idKassa != null)
          : [];
        setItems(rows);
        setSelectedId((prev) => {
          if (prev != null && rows.some((r) => r.idKassa === prev)) return prev;
          return null;
        });
      })
      .catch((e) => {
        if (requestId !== listReqRef.current) return;
        setError(e.message || "Не удалось загрузить кассы");
        setItems([]);
      })
      .finally(() => {
        if (requestId === listReqRef.current) setLoadingList(false);
      });
  }, []);

  useEffect(() => {
    if (canView) loadList();
  }, [canView, loadList]);

  const filteredItems = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.kassirName || "").toLowerCase().includes(q) ||
        (t.sn || "").toLowerCase().includes(q)
    );
  }, [items, nameFilter]);

  const selectedItem = useMemo(
    () => items.find((t) => t.idKassa === selectedId) || null,
    [items, selectedId]
  );

  const openCreate = () => {
    if (!canEdit) return;
    setDrawerIsNew(true);
    setDrawerId(null);
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    if (row?.idKassa == null) return;
    setSelectedId(row.idKassa);
    setDrawerIsNew(false);
    setDrawerId(row.idKassa);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const onDrawerSaved = (saved) => {
    const id = toNumber(pick(saved, "idKassa", "IdKassa"));
    loadList().then(() => {
      if (id != null) {
        setSelectedId(id);
        setDrawerId(id);
        setDrawerIsNew(false);
      }
    });
  };

  const onDel = () => {
    if (!selectedItem || !canEdit) return;
    Modal.confirm({
      title: "Удалить?",
      content: `Касса «${selectedItem.name || selectedItem.idKassa}» будет удалена.`,
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        deleteSprKassa(selectedItem.idKassa)
          .then(() => {
            message.success("Касса удалена");
            if (drawerId === selectedItem.idKassa) closeDrawer();
            setSelectedId(null);
            return loadList();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const columns = [
    {
      title: "",
      label: "Статус",
      key: "status",
      width: 44,
      align: "center",
      render: (_, row) => {
        const hidden = row.isHide === 1;
        const label = hidden ? "Скрыта" : "Активна";
        return (
          <Tooltip title={label}>
            <span
              className="spr-kassa__status-dot"
              data-tone={hidden ? "off" : "on"}
              aria-label={label}
            />
          </Tooltip>
        );
      }
    },
    {
      title: "#",
      dataIndex: "idKassa",
      key: "idKassa",
      width: 64,
      align: "right"
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      render: (name, row) => name || `#${row.idKassa}`
    },
    {
      title: "Кассир",
      dataIndex: "kassirName",
      key: "kassirName",
      width: 160,
      ellipsis: true
    },
    {
      title: "%",
      dataIndex: "prc",
      key: "prc",
      width: 64,
      align: "right"
    },
    {
      title: "Серийный номер",
      dataIndex: "sn",
      key: "sn",
      width: 140,
      ellipsis: true
    },
    {
      title: "Вид ККМ",
      dataIndex: "vid",
      key: "vid",
      width: 180,
      ellipsis: true,
      render: (vid) => VID_KKM_LABELS[vid] || (vid != null ? String(vid) : "")
    },
    {
      title: "Терминал",
      dataIndex: "isTerm",
      key: "isTerm",
      width: 84,
      align: "center",
      render: (v) => (v === 1 ? <CheckOutlined /> : null)
    },
    {
      title: "Филиал",
      dataIndex: "branchName",
      key: "branchName",
      width: 140,
      ellipsis: true,
      render: (name, row) => name || (row.idBranch ? `#${row.idBranch}` : "")
    }
  ];

  if (!canView) {
    return (
      <div className="spr-kassa">
        <Alert type="warning" showIcon message="Нет права на просмотр справочника касс" />
      </div>
    );
  }

  return (
    <div className="spr-kassa">
      {error ? (
        <Alert className="spr-kassa__error" type="error" showIcon message={error} />
      ) : null}

      <div className="spr-kassa__body">
        <div className="spr-kassa__list">
          <div className="spr-kassa__toolbar">
            <div className="spr-kassa__extra-actions">
              <Tooltip title="Создать">
                <span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!canEdit}
                    aria-label="Создать"
                    onClick={openCreate}
                  />
                </span>
              </Tooltip>
              <Tooltip title={selectedItem ? "Изменить" : "Выберите запись"}>
                <span>
                  <Button
                    icon={<EditOutlined />}
                    disabled={!selectedItem}
                    aria-label="Изменить"
                    onClick={() => openEdit(selectedItem)}
                  />
                </span>
              </Tooltip>
              <Tooltip title={selectedItem ? "Удалить" : "Выберите запись"}>
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!canEdit || !selectedItem}
                    aria-label="Удалить"
                    onClick={onDel}
                  />
                </span>
              </Tooltip>
            </div>
            <Input
              allowClear
              variant="borderless"
              className="spr-kassa__filter"
              placeholder="Фильтр по наименованию"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>

          <div className="spr-kassa__list-body">
            {loadingList ? (
              <div className="spr-kassa__center">
                <Spin />
              </div>
            ) : (
              <Table
                className="spr-kassa__table"
                size="small"
                rowKey="idKassa"
                pagination={false}
                columns={columns}
                dataSource={filteredItems}
                rowClassName={(row) =>
                  [
                    "spr-kassa__row",
                    row.isHide === 1 ? "spr-kassa__row--hidden" : "",
                    row.idKassa === selectedId ? "spr-kassa__row--selected" : "",
                    drawerOpen && row.idKassa === drawerId ? "spr-kassa__row--editing" : ""
                  ]
                    .filter(Boolean)
                    .join(" ")
                }
                onRow={(row) => ({
                  onClick: () => {
                    setSelectedId(row.idKassa);
                    openEdit(row);
                  }
                })}
                locale={{
                  emptyText: items.length ? "Ничего не найдено" : "Нет касс"
                }}
              />
            )}
          </div>
        </div>
      </div>

      <SprKassaDrawer
        open={drawerOpen}
        idKassa={drawerId}
        isNew={drawerIsNew}
        canEdit={canEdit}
        onClose={closeDrawer}
        onSaved={onDrawerSaved}
      />
    </div>
  );
};

export default SprKassa;
